const STRIPE_RETURN_PATH_KEY = "stripeReturnPath";
const STRIPE_RETURN_PATH_STORED_AT_KEY = "stripeReturnPathStoredAt";
const STRIPE_RETURN_PATH_TTL_MS = 1000 * 60 * 60; // 1 hour

const appendPrefilledEmail = (baseUrl: string, email?: string | null): string => {
  if (!email) {
    return baseUrl;
  }

  try {
    const parsed = new URL(baseUrl);
    parsed.searchParams.set("locked_prefilled_email", email);
    return parsed.toString();
  } catch {
    const separator = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${separator}locked_prefilled_email=${encodeURIComponent(email)}`;
  }
};

const appendClientReferenceId = (
  url: string,
  clientReferenceId?: string | null,
): string => {
  if (!clientReferenceId) {
    return url;
  }

  try {
    const parsed = new URL(url);
    parsed.searchParams.set("client_reference_id", clientReferenceId);
    return parsed.toString();
  } catch {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}client_reference_id=${encodeURIComponent(clientReferenceId)}`;
  }
};

export type CreditPurchaseSource = "billing_page" | "sidebar" | "report_modal";

/** Payment Links carry no per-click metadata, so the surface a credit purchase
 *  started from rides along on `client_reference_id` for the webhook to parse. */
export const buildCreditPurchaseReference = (
  workspaceId: number | string | null | undefined,
  source: CreditPurchaseSource,
): string =>
  workspaceId == null ? `src_${source}` : `workspace_${workspaceId}-src_${source}`;

export const redirectToStripeWithReturnPath = (
  stripeBaseUrl: string,
  options: {
    email?: string | null;
    returnPath: string;
    clientReferenceId?: string | null;
  },
): void => {
  if (!stripeBaseUrl) {
    return;
  }

  sessionStorage.setItem(STRIPE_RETURN_PATH_KEY, options.returnPath);
  sessionStorage.setItem(STRIPE_RETURN_PATH_STORED_AT_KEY, String(Date.now()));

  const prefilledUrl = appendPrefilledEmail(stripeBaseUrl, options.email);
  const finalUrl = appendClientReferenceId(prefilledUrl, options.clientReferenceId);
  window.location.assign(finalUrl);
};

export const consumeStripeReturnPath = (currentPath: string): string | null => {
  const returnPath = sessionStorage.getItem(STRIPE_RETURN_PATH_KEY);
  const storedAtRaw = sessionStorage.getItem(STRIPE_RETURN_PATH_STORED_AT_KEY);

  if (!returnPath) {
    return null;
  }

  sessionStorage.removeItem(STRIPE_RETURN_PATH_KEY);
  sessionStorage.removeItem(STRIPE_RETURN_PATH_STORED_AT_KEY);

  const storedAt = storedAtRaw ? Number(storedAtRaw) : NaN;
  if (Number.isFinite(storedAt) && Date.now() - storedAt > STRIPE_RETURN_PATH_TTL_MS) {
    return null;
  }

  if (returnPath === currentPath) {
    return null;
  }

  return returnPath;
};